/**
 * REKKIE Eval & Test-Suites API client.
 *
 * Talks to the backend routes in backend/routes/eval.js (mounted at /api/eval,
 * gated by the `qa` product). Same auth pattern as the other service modules:
 * a Firebase ID token from AuthContext on every request.
 */
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

const EVAL_BASE = '/eval';

/** JSON request helper. `body` is omitted when null. */
async function request(method, endpoint, body = null) {
  const idToken = await getIdToken();
  if (!idToken) {
    throw new Error('Not authenticated. Please log in.');
  }
  const options = { method, headers: getAuthHeaders(idToken) };
  if (body !== null) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(buildApiUrl(`${EVAL_BASE}${endpoint}`), options);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || `Request failed (${response.status})`);
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

/** Multipart upload helper — the browser sets the multipart Content-Type. */
async function uploadFile(endpoint, file) {
  const idToken = await getIdToken();
  if (!idToken) {
    throw new Error('Not authenticated. Please log in.');
  }
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(buildApiUrl(`${EVAL_BASE}${endpoint}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
    body: form,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || `Upload failed (${response.status})`);
  }
  return response.json();
}

// ─── Evals ────────────────────────────────────────────────────────────
export const listEvals = () => request('GET', '/evals');
export const createEval = (payload) => request('POST', '/evals', payload);
export const getEval = (id) => request('GET', `/evals/${id}`);
export const updateEval = (id, patch) => request('PATCH', `/evals/${id}`, patch);
export const saveCaseResult = (evalId, caseId, result) => request(
  'PUT', `/evals/${evalId}/cases/${caseId}/result`, result,
);
export const getCaseHistory = (evalId, caseId) => request(
  'GET', `/evals/${evalId}/cases/${caseId}/history`,
);

// ─── Suites ───────────────────────────────────────────────────────────
export const listSuites = () => request('GET', '/suites');
export const getSuite = (id, version) => request(
  'GET', version ? `/suites/${id}?version=${version}` : `/suites/${id}`,
);
export const createSuite = (draft) => request('POST', '/suites', draft);
export const saveSuite = (id, draft) => request('PUT', `/suites/${id}`, draft);
export const archiveSuite = (id) => request('DELETE', `/suites/${id}`);

// ─── Version lists ────────────────────────────────────────────────────
export const getVersions = () => request('GET', '/versions');
export const addVersion = (kind, value) => request('POST', '/versions', { kind, value });
export const removeVersion = (kind, value) => request(
  'DELETE', `/versions/${kind}/${encodeURIComponent(value)}`,
);

// ─── Media ────────────────────────────────────────────────────────────
export const uploadSuiteMedia = (suiteId, file) => uploadFile(`/suites/${suiteId}/media`, file);
export const uploadEvidence = (evalId, caseId, file) => uploadFile(
  `/evals/${evalId}/cases/${caseId}/evidence`, file,
);
